// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { AdvancedOrderEntry } from './AdvancedOrderEntry';
import { AssetBalancesPanel } from './AssetBalancesPanel';
import { CreateAlertPanel } from './CreateAlertPanel';
import { EventsPanel } from './EventsPanel';
import { FilledOrdersPanel } from './FilledOrdersPanel';
import { InformerPanel } from './InformerPanel';
import { OrderHistoryPanel } from './OrderHistoryPanel';
import { OrdersPanel } from './OrdersPanel';
import { PositionsPanel } from './PositionsPanel';
import { ProductsPanel } from './ProductsPanel';
import { ReportsPanel } from './ReportsPanel';
import { RiskCalculator } from './RiskCalculator';
import { TimeSalesPanel } from './TimeSalesPanel';
import { SymbolInfoPanel } from './SymbolInfoPanel';
import { MarginOEControl } from './MarginOEControl';
import { MarketDepthPanel } from './MarketDepthPanel';
import { ChartPanel } from './ChartPanel';
import { GridPanel } from './GridPanel';
import { FullLogsPanel } from './FullLogsPanel';
import { WebAdminPanel } from './WebAdminPanel';
import { InstrumentHolidaysScreen } from './InstrumentHolidaysScreen';
import { SavedOrdersPanel } from './SavedOrdersPanel';
import { StatementPanel } from './StatementPanel';
import { TradingCentral } from './TradingCentral';
import { ExternalScreen } from '../screen/ExternalScreen';
import { AssetsPanel } from './AssetsPanel';
import { PositionsBalancePanel } from './PositionsBalancePanel';
import { NewsPanel } from './NewsPanel';
import { TerceraClosePositionScreen } from '../screen/TerceraClosePositionScreen';
import { ModifyPositionScreen } from '../screen/ModifyPositionScreen';
import { ModifyOrderScreen } from '../screen/ModifyOrderScreen';
import { EditPositionScreen } from '../screen/EditPositionScreen';
import { PositionExerciseRequestScreen } from '../screen/PositionExerciseRequestScreen';
import { DockPlaceConst, DockSystemInstance } from '../DockSystem';
import { MessageBoxType } from '@shared/utils/MessageBoxUtils';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { PanelLocKeys, PanelNames, ScreensNames } from '../UtilsClasses/FactoryConstants';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DataCache } from '@shared/commons/DataCache';
import { WorkSpaceManager } from '../WorkSpace/WorkSpaceManager';
import { ClosePositionsMutually } from './ClosePositionsMutually';
import { TradingViewChartPanel } from './TradingViewChartPanel';
import { GainersLosersPanel } from './GainersLosersPanel';
import { HighLowPanel } from './HighLowPanel';
import { HeatmapPanel } from './HeatmapPanel';
import { OptionMasterPanel } from './OptionMaster/OptionMasterPanel';
import { CopyTradingPanel } from './CopyTradingPanel';
import { ObjectManagerScreen } from '../screen/ObjectManagerScreen';
import { WebhooksScreen } from '../screen/WebhooksScreen';
import { WebhookCreatedScreen } from '../screen/WebhookCreatedScreen';
class _Factory {
    //
    // Создание всех панелей
    //
    addPanel(type, from, afterCreationCallback) {
        if (isNullOrUndefined(WorkSpaceManager.currentWorkspace)) {
            return;
        }
        const fromRestore = from === FactoryAddPanelFrom.Restore;
        const value = this.GetThrottlingOperationValue(type);
        if (!fromRestore && value.throttlingOperation !== ThrottlingOperation.CanAddPanel) {
            return;
        }
        const newPanel = this.createPanel(type);
        const skipLock = type === PanelNames.ReportsPanel ||
            type === PanelNames.OrderEntry ||
            type === PanelNames.AdvancedOrderEntry ||
            type === PanelNames.RiskCalculator ||
            type === PanelNames.NewAccountDetailsPanel ||
            type === PanelNames.SymbolInfoPanel;
        if (WorkSpaceManager.currentWorkspace.locked && !skipLock && !fromRestore) {
            WorkSpaceManager.processUndock();
            return;
        }
        this.dockSystemOperations(newPanel, fromRestore);
        this.addPanelToWs(newPanel);
        if (!isNullOrUndefined(afterCreationCallback)) {
            afterCreationCallback(newPanel);
        }
        return newPanel;
    }
    createPanel(type) {
        let newPanel;
        switch (type) {
            case PanelNames.PositionsPanel:
                newPanel = new PositionsPanel();
                break;
            case PanelNames.OrdersPanel:
                newPanel = new OrdersPanel();
                break;
            case PanelNames.EventsPanel:
                newPanel = new EventsPanel();
                break;
            case PanelNames.OrderHistoryPanel:
                newPanel = new OrderHistoryPanel();
                break;
            case PanelNames.FilledOrdersPanel:
                newPanel = new FilledOrdersPanel();
                break;
            case PanelNames.AssetBalancesPanel:
                newPanel = new AssetBalancesPanel();
                break;
            case PanelNames.InformerPanel:
                newPanel = new InformerPanel();
                break;
            case PanelNames.GainersLosersPanel:
                newPanel = new GainersLosersPanel();
                break;
            case PanelNames.HighLowPanel:
                newPanel = new HighLowPanel();
                break;
            case PanelNames.CreateAlertPanel:
                newPanel = new CreateAlertPanel();
                break;
            case PanelNames.AdvancedOrderEntry:
                newPanel = new AdvancedOrderEntry();
                break;
            case PanelNames.RiskCalculator:
                newPanel = new RiskCalculator();
                break;
            case PanelNames.TimeSalesPanel:
                newPanel = new TimeSalesPanel();
                break;
            case PanelNames.ReportsPanel:
                newPanel = new ReportsPanel();
                break;
            case PanelNames.ProductsPanel:
                newPanel = new ProductsPanel();
                break;
            case PanelNames.SymbolInfoPanel:
                newPanel = new SymbolInfoPanel();
                break;
            case PanelNames.MarginOEControl:
                newPanel = new MarginOEControl();
                break;
            case PanelNames.MarketDepthPanel:
                newPanel = new MarketDepthPanel();
                break;
            case PanelNames.ChartPanel:
                newPanel = new ChartPanel();
                break;
            case PanelNames.TradingViewChartPanel:
                newPanel = new TradingViewChartPanel();
                break;
            case PanelNames.GridPanel:
                newPanel = new GridPanel();
                break;
            case PanelNames.FullLogsPanel:
                newPanel = new FullLogsPanel();
                break;
            case PanelNames.WebAdminPanel:
                newPanel = new WebAdminPanel();
                break;
            case PanelNames.InstrumentHolidaysScreen:
                newPanel = new InstrumentHolidaysScreen();
                break;
            case PanelNames.SavedOrdersPanel:
                newPanel = new SavedOrdersPanel();
                break;
            case PanelNames.StatementPanel:
                newPanel = new StatementPanel();
                break;
            case PanelNames.TradingCentral:
                newPanel = new TradingCentral();
                break;
            case PanelNames.ExternalScreen:
                newPanel = new ExternalScreen();
                break;
            case PanelNames.AssetsPanel:
                newPanel = new AssetsPanel();
                break;
            case PanelNames.PositionsBalancePanel:
                newPanel = new PositionsBalancePanel();
                break;
            case PanelNames.NewsPanel:
                newPanel = new NewsPanel();
                break;
            case PanelNames.ClosePositionsMutually:
                newPanel = new ClosePositionsMutually();
                break;
            case PanelNames.HeatmapPanel:
                newPanel = new HeatmapPanel();
                break;
            case PanelNames.OptionMasterPanel:
                newPanel = new OptionMasterPanel();
                break;
            case PanelNames.CopyTradingPanel:
                newPanel = new CopyTradingPanel();
                break;
            default: {
                // мы это не создаём пока или type не указан
                break;
            }
        }
        return newPanel;
    }
    dockSystemOperations(newPanel, fromRestore) {
        newPanel.CustomPreparation();
        const dockPlace = this.getDefaultPlace(newPanel.getType());
        if (!fromRestore || !newPanel.get('dockablePanel')) {
            MainWindowManager.AddControl(newPanel);
        }
        else {
            DockSystemInstance.goInProcces(newPanel, dockPlace);
            newPanel.on('complete', function () {
                if (this.get('workSpaceId') !== null) {
                    DockSystemInstance.setPlace(this.get('workSpaceId'), dockPlace);
                }
            });
        }
        if (!fromRestore && newPanel.get('dockablePanel')) {
            DockSystemInstance.addPanelOnDock(newPanel, dockPlace, WorkSpaceManager.currentWorkspace.locked);
        }
    }
    addPanelToWs(newPanel) {
        // save
        WorkSpaceManager.currentWorkspace.addPanel(newPanel);
    }
    addScreen(type) {
        let newControl = null;
        let isAddNewControl = true;
        let isFocusNewControl = false;
        switch (type) {
            // case ScreensNames.ModifyScreen:
            //     newControl = new TerceraModifyScreen()
            //     break
            case ScreensNames.ClosePositionScreen:
                newControl = new TerceraClosePositionScreen();
                isAddNewControl = false;
                break;
            case ScreensNames.ModifyPositionScreen:
                newControl = new ModifyPositionScreen();
                isAddNewControl = false;
                break;
            case ScreensNames.ModifyOrderScreen:
                newControl = new ModifyOrderScreen();
                break;
            case ScreensNames.EditPositionScreen:
                newControl = new EditPositionScreen();
                break;
            case ScreensNames.PositionExerciseRequestScreen:
                newControl = new PositionExerciseRequestScreen();
                break;
            case ScreensNames.ObjectManagerScreen:
                newControl = new ObjectManagerScreen();
                break;
            case ScreensNames.WebhooksScreen:
                newControl = this.getScreen(ScreensNames.WebhooksScreen);
                isAddNewControl = newControl === null;
                isFocusNewControl = true;
                if (isAddNewControl) {
                    newControl = new WebhooksScreen();
                }
                break;
            case ScreensNames.WebhookCreatedScreen:
                newControl = new WebhookCreatedScreen();
                isFocusNewControl = true;
                break;
        }
        if (isAddNewControl) {
            MainWindowManager.AddControl(newControl);
        }
        if (isFocusNewControl) {
            newControl.setFocus();
        }
        return newControl;
    }
    getScreen(type) {
        return MainWindowManager.GetScreen(type);
    }
    getDefaultPlace(type) {
        return _Factory.PanelPlaces[type];
    }
    GetThrottlingOperationValue(panelType, aboutRows = false) {
        const result = { throttlingOperation: ThrottlingOperation.CanAddPanel, rowsNumber: -1 };
        let ruleStringValue;
        switch (panelType) {
            case PanelNames.ChartPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_CHART_COUNT, -1);
                break;
            case PanelNames.MarketDepthPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_MARKETDEPTH_COUNT, -1);
                break;
            case PanelNames.TimeSalesPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_TIMEANDSALES_COUNT, -1);
                break;
            case PanelNames.InformerPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(aboutRows
                    ? RulesSet.VALUE_THROTLING_PANEL_LIMIT_WATCHLIST_ROWS
                    : RulesSet.VALUE_THROTLING_PANEL_LIMIT_WATCHLIST_COUNT, -1);
                break;
            case PanelNames.SavedOrdersPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(aboutRows
                    ? RulesSet.VALUE_THROTLING_PANEL_LIMIT_SAVEDORDERS_ROWS
                    : RulesSet.VALUE_THROTLING_PANEL_LIMIT_SAVEDORDERS_COUNT, -1);
                break;
            case PanelNames.TradingViewChartPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_PANEL_LIMIT_CHART_TV_COUNT, -1);
                break;
            case PanelNames.GridPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_GRID_COUNT, -1);
                break;
            case PanelNames.OptionMasterPanel:
                ruleStringValue = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_THROTLING_PANEL_LIMIT_OPTIONMASTER_COUNT, -1);
                break;
        }
        if (aboutRows) {
            result.throttlingOperation = ThrottlingOperation.CanAddRows;
            result.rowsNumber = ruleStringValue;
            ruleStringValue = -1;
        }
        const panelNum = WorkSpaceManager.currentWorkspace.numberOfPanelByType(panelType);
        if (ruleStringValue !== -1 && panelNum >= ruleStringValue) {
            result.throttlingOperation = ThrottlingOperation.Reject;
            TerceraMessageBox.Show(Resources.getResource('workspace.information'), Resources.getResource('Throtling.Count.firstPart') +
                ' ' +
                Resources.getResource(PanelLocKeys[panelType]) +
                ' ' +
                Resources.getResource('Throtling.Count.secondPart'), MessageBoxType.Info, null, null, false, true);
        }
        return result;
    }
}
// тут указываем кто куда по умолчанию дочится
_Factory.PanelPlacesMask = [
    {
        place: DockPlaceConst.Middle,
        panels: [
            PanelNames.ChartPanel,
            PanelNames.TradingViewChartPanel,
            PanelNames.TradingCentral,
            PanelNames.GridPanel,
            PanelNames.HeatmapPanel,
            PanelNames.OptionMasterPanel
        ]
    },
    {
        place: DockPlaceConst.Up,
        panels: []
    },
    {
        place: DockPlaceConst.Down,
        panels: [
            PanelNames.OrderHistoryPanel,
            PanelNames.PositionsPanel,
            PanelNames.OrdersPanel,
            PanelNames.EventsPanel,
            PanelNames.AssetBalancesPanel,
            PanelNames.FilledOrdersPanel,
            PanelNames.SavedOrdersPanel,
            PanelNames.StatementPanel,
            PanelNames.FullLogsPanel,
            PanelNames.WebAdminPanel,
            PanelNames.PositionsBalancePanel,
            PanelNames.NewsPanel,
            PanelNames.AssetsPanel,
            PanelNames.ProductsPanel
        ]
    },
    {
        place: DockPlaceConst.Left,
        panels: [
            PanelNames.OrderEntry,
            PanelNames.AdvancedOrderEntry,
            PanelNames.RiskCalculator,
            PanelNames.ReportsPanel,
            PanelNames.NewAccountDetailsPanel,
            PanelNames.InformerPanel,
            PanelNames.SymbolInfoPanel,
            PanelNames.GainersLosersPanel,
            PanelNames.HighLowPanel
        ]
    },
    {
        place: DockPlaceConst.Right,
        panels: [PanelNames.TimeSalesPanel, PanelNames.MarketDepthPanel, PanelNames.ExternalScreen]
    }
];
_Factory.PanelPlaces = (function () {
    const result = {};
    const len = _Factory.PanelPlacesMask.length;
    for (let i = 0; i < len; i++) {
        const curPlaceObj = _Factory.PanelPlacesMask[i];
        const curPlace = curPlaceObj.place;
        const curPanels = curPlaceObj.panels;
        const panelsLen = curPanels.length;
        for (let j = 0; j < panelsLen; j++) {
            result[curPanels[j]] = curPlace;
        }
    }
    return result;
})();
export var FactoryAddPanelFrom;
(function (FactoryAddPanelFrom) {
    FactoryAddPanelFrom[FactoryAddPanelFrom["Restore"] = 0] = "Restore";
    FactoryAddPanelFrom[FactoryAddPanelFrom["Throttling"] = 1] = "Throttling";
})(FactoryAddPanelFrom || (FactoryAddPanelFrom = {}));
// create instance
export class UpdatePackageInfo {
    constructor() {
        this.addedRemovedRows = [];
        this.updatedRows = [];
        this.additionalRowsData = [];
        this.additionalTableData = null;
    }
}
UpdatePackageInfo.ADD_ROW = 0;
UpdatePackageInfo.REMOVE_ROW = 1;
export var ThrottlingOperation;
(function (ThrottlingOperation) {
    ThrottlingOperation[ThrottlingOperation["CanAddPanel"] = 0] = "CanAddPanel";
    ThrottlingOperation[ThrottlingOperation["Reject"] = 1] = "Reject";
    ThrottlingOperation[ThrottlingOperation["ForbidPanel"] = 2] = "ForbidPanel";
    ThrottlingOperation[ThrottlingOperation["CanAddRows"] = 3] = "CanAddRows";
})(ThrottlingOperation || (ThrottlingOperation = {}));
export const Factory = new _Factory();
